<template>
  <div class="orders-filters">
    <template v-if="isLoading">
      <SkeletonLoader
        v-if="isLoading"
        show-animation
        data-testid="orders-filters-loader"
        class="orders-filters__loader"
      >
        <div
          v-for="index in 2"
          :key="index"
          class="orders-filters__loader__filter"
        />
      </SkeletonLoader>
    </template>
    <template v-else>
      <OrderTypeFilter
        class="orders-filters__filter"
        :value="filters[OrderFiltersKeys.Type].value"
        :options="filters[OrderFiltersKeys.Type].options"
        :disabled="!hasFilters"
        @on-select-filter="
          value => emitOnFilterSelection(OrderFiltersKeys.Type, value)
        "
      />
      <InstrumentFilter
        class="orders-filters__filter"
        :value="filters[OrderFiltersKeys.Instrument].value"
        :options="instruments"
        :status="instrumentsFilterStatus"
        :result-label="resultLabel"
        :fetch-options="handleOnSearch"
        @on-select-filter="handleInstrumentSelection"
        @on-retry="handleOnRetry"
        @on-selection-modal-open="handleInstrumentSelectionModalOpen"
        @on-search="handleOnSearch"
        @on-close-modal="handleCloseSelectionModal"
        @on-cancel-modal="handleCancelSelectionModal"
      />
      <DateFilter
        class="orders-filters__filter"
        :value="filters[OrderFiltersKeys.Date].value"
        :options="filters[OrderFiltersKeys.Date].options"
        :disabled="!hasFilters"
        @on-select-filter="
          value => emitOnFilterSelection(OrderFiltersKeys.Date, value)
        "
      />
      <OrderStatusFilter
        class="orders-filters__filter"
        :value="filters[OrderFiltersKeys.Status].value"
        :options="filters[OrderFiltersKeys.Status].options"
        :disabled="!hasFilters"
        @on-select-filter="
          value => emitOnFilterSelection(OrderFiltersKeys.Status, value)
        "
      />
      <button
        v-if="hasFilterSelected"
        class="orders-filters__button"
        @click="emitOnClearFilters"
      >
        {{ cleanFiltersButtonLabel }}
      </button>
    </template>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Emit } from 'vue-property-decorator';

import { OrderStatusFilter } from './components/OrderStatusFilter';
import { OrderTypeFilter } from './components/OrderTypeFilter';
import { InstrumentFilter } from '@/modules/trade/components/InstrumentFilter';
import { DateFilter } from './components/DateFilter';
import { SkeletonLoader } from '@warrenbrasil/nebraska-web';

import {
  InstrumentFilter as IInstrumentFilter,
  getCustomerOrdersInstruments
} from '@/modules/trade/services/orders';
import { OrderFiltersKeys, Filters } from '@/modules/trade/views/Orders/types';
import { InstrumentFilterStatus } from '@/modules/trade/components/InstrumentFilter/types';
import { logError } from '@/modules/trade/helpers/logs';
import * as trackOrdersEvents from '@/modules/trade/views/Orders/trackers';

@Component({
  components: {
    DateFilter,
    OrderTypeFilter,
    OrderStatusFilter,
    SkeletonLoader,
    InstrumentFilter
  }
})
export default class OrdersFilters extends Vue {
  @Prop({ type: Object, required: true })
  readonly filters!: Filters;

  @Prop({ type: Boolean, default: false })
  readonly isLoading!: boolean;

  @Prop({ type: Boolean, required: true })
  readonly hasFilterSelected!: boolean;

  instrumentsFilterStatus = InstrumentFilterStatus.Loading;
  instruments: IInstrumentFilter[] | null = null;
  resultLabel = 'Meus ativos';
  abortSignal: AbortController | null = null;

  readonly OrderFiltersKeys = OrderFiltersKeys;

  @Emit('on-filter-selection')
  emitOnFilterSelection(_filterKey: OrderFiltersKeys, _value: string) {}

  @Emit('on-clear-filters')
  emitOnClearFilters() {}

  get cleanFiltersButtonLabel() {
    const filtersKeys = Object.values(OrderFiltersKeys);

    const filtersWithSelectedValue = filtersKeys.filter(
      filterKey => this.filters[filterKey].value !== null
    );

    return filtersWithSelectedValue.length > 1
      ? 'Limpar filtros'
      : 'Limpar filtro';
  }

  get hasFilters() {
    const filtersKeys = Object.values(OrderFiltersKeys);

    return filtersKeys.some(filterKey => {
      return !!this.filters[filterKey].options?.length;
    });
  }

  handleInstrumentSelection(value: string) {
    trackOrdersEvents.trackInstrumentSelectionApplyClick();
    this.emitOnFilterSelection(OrderFiltersKeys.Instrument, value);
  }

  handleCloseSelectionModal() {
    trackOrdersEvents.trackInstrumentSelectionClose();
  }

  handleCancelSelectionModal() {
    trackOrdersEvents.trackInstrumentSelectionCancelClick();
  }

  async handleOnRetry() {
    trackOrdersEvents.trackInstrumentSelectionErrorRetryClick();
    await this.handleOnSearch();
  }

  async handleInstrumentSelectionModalOpen() {
    trackOrdersEvents.trackInstrumentSelectionModalLoad();
    await this.handleOnSearch();
  }

  async handleOnSearch(text = '', page = 1) {
    if (this.abortSignal) {
      this.abortSignal.abort();
    }

    try {
      const isFirstPage = page === 1;
      if (isFirstPage)
        this.instrumentsFilterStatus = InstrumentFilterStatus.Loading;

      this.abortSignal = new AbortController();

      const { items } = await getCustomerOrdersInstruments(
        {
          query: text,
          page,
          itemsPerPage: 10
        },
        this.abortSignal.signal
      );

      this.instruments = isFirstPage
        ? items
        : [...(this.instruments || []), ...items];

      this.resultLabel = text ? `Resultados para "${text}"` : 'Meus ativos';

      this.instrumentsFilterStatus = InstrumentFilterStatus.Default;
    } catch (error) {
      this.instrumentsFilterStatus = InstrumentFilterStatus.Error;
      trackOrdersEvents.trackInstrumentSelectionErrorLoad();
      logError(error, 'request_customer_orders_instruments_error');
    }
  }
}
</script>

<style lang="less" scoped>
.orders-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;

  &__filter {
    margin-bottom: @size-spacing-x300;

    &:not(:last-of-type) {
      margin-right: @size-spacing-x300;
    }
  }

  &__button {
    .text-p-5();
    margin-bottom: @size-spacing-x300;
    text-decoration: underline;
    padding: 0;
    margin-left: @size-spacing-x300;
  }

  &__loader {
    display: flex;

    &__filter {
      &:not(:last-of-type) {
        margin-right: @size-spacing-x300;
      }

      width: 100px;
      height: 32px;
      border-radius: @size-radius-max;
      margin-right: @size-spacing-x300;
      margin-bottom: @size-spacing-x300;
    }
  }
}
</style>
